import type { PcapPeriodResult, PcapPeriodSet } from './types';

function categoryValue(
  categories: Array<{ label: string; value: number }>,
  label: string,
): number {
  return categories.find((c) => c.label === label)?.value ?? 0;
}

export default function buildPcapTableRows(
  lp: PcapPeriodSet,
  fund: PcapPeriodSet,
) {
  function row(
    label: string,
    getValue: (p: PcapPeriodResult) => number,
    opts?: {
      isTotal?: boolean;
      isSectionHeader?: boolean;
      subtitle?: string;
      isSubtleBackground?: boolean;
    },
  ) {
    if (opts?.isSectionHeader) {
      return {
        label,
        isSectionHeader: true,
        lpQtd: 0,
        lpYtd: 0,
        lpSinceInception: 0,
        fundQtd: 0,
        fundYtd: 0,
        fundSinceInception: 0,
      };
    }
    return {
      label,
      subtitle: opts?.subtitle,
      isTotal: opts?.isTotal,
      isSubtleBackground: opts?.isSubtleBackground,
      lpQtd: getValue(lp.qtd),
      lpYtd: getValue(lp.ytd),
      lpSinceInception: getValue(lp.sinceInception),
      fundQtd: getValue(fund.qtd),
      fundYtd: getValue(fund.ytd),
      fundSinceInception: getValue(fund.sinceInception),
    };
  }

  const capitalMovementRows = [
    row('Capital movement', () => 0, { isSectionHeader: true }),
    row('Total commitment', (p) => p.capitalMovement.totalCommitment),
    row(
      'Unfunded commitment',
      (p) => p.capitalMovement.unfundedCommitmentAtStart,
      {
        subtitle: 'Start of period',
        isSubtleBackground: true,
      },
    ),
    row(
      'Contributions',
      (p) => p.capitalMovement.contributionsInsideCommitment,
      {
        subtitle: 'Inside commitment',
      },
    ),
    row(
      'Recallable distributions',
      (p) => p.capitalMovement.recallableDistributions,
    ),
    row(
      'Unfunded commitment',
      (p) => p.capitalMovement.unfundedCommitmentAtEnd,
      {
        subtitle: 'End of period',
        isSubtleBackground: true,
      },
    ),
    row(
      'Contributions',
      (p) => p.capitalMovement.contributionsOutsideCommitment,
      {
        subtitle: 'Outside commitment',
      },
    ),
    row(
      'Non-recallable distributions',
      (p) => p.capitalMovement.nonRecallableDistributions,
    ),
    row('Net capital movement', (p) => p.capitalMovement.netCapitalMovement, {
      isTotal: true,
      subtitle: 'Contributions minus distributions',
    }),
  ];

  const allExpenseLabels = new Set<string>([
    ...lp.qtd.expenses.byCategory.map((c) => c.label),
    ...lp.ytd.expenses.byCategory.map((c) => c.label),
    ...lp.sinceInception.expenses.byCategory.map((c) => c.label),
    ...fund.qtd.expenses.byCategory.map((c) => c.label),
    ...fund.ytd.expenses.byCategory.map((c) => c.label),
    ...fund.sinceInception.expenses.byCategory.map((c) => c.label),
  ]);

  const expenseRows = [
    row('Expenses', () => 0, { isSectionHeader: true }),
    row('Management fees', (p) => p.expenses.managementFees, {
      subtitle: 'Before offset',
    }),
    ...[...allExpenseLabels].map((label) => ({
      label: 'Partnership expenses',
      subtitle: label,
      lpQtd: categoryValue(lp.qtd.expenses.byCategory, label),
      lpYtd: categoryValue(lp.ytd.expenses.byCategory, label),
      lpSinceInception: categoryValue(
        lp.sinceInception.expenses.byCategory,
        label,
      ),
      fundQtd: categoryValue(fund.qtd.expenses.byCategory, label),
      fundYtd: categoryValue(fund.ytd.expenses.byCategory, label),
      fundSinceInception: categoryValue(
        fund.sinceInception.expenses.byCategory,
        label,
      ),
    })),
    row('Total expenses', (p) => p.expenses.totalExpenses, { isTotal: true }),
  ];

  const allIncomeLabels = new Set<string>([
    ...lp.qtd.income.byCategory.map((c) => c.label),
    ...lp.ytd.income.byCategory.map((c) => c.label),
    ...lp.sinceInception.income.byCategory.map((c) => c.label),
    ...fund.qtd.income.byCategory.map((c) => c.label),
    ...fund.ytd.income.byCategory.map((c) => c.label),
    ...fund.sinceInception.income.byCategory.map((c) => c.label),
  ]);

  const incomeRows = [
    row('Income', () => 0, { isSectionHeader: true }),
    ...[...allIncomeLabels].map((label) => ({
      label,
      lpQtd: categoryValue(lp.qtd.income.byCategory, label),
      lpYtd: categoryValue(lp.ytd.income.byCategory, label),
      lpSinceInception: categoryValue(
        lp.sinceInception.income.byCategory,
        label,
      ),
      fundQtd: categoryValue(fund.qtd.income.byCategory, label),
      fundYtd: categoryValue(fund.ytd.income.byCategory, label),
      fundSinceInception: categoryValue(
        fund.sinceInception.income.byCategory,
        label,
      ),
    })),
    row('Total income', (p) => p.income.totalIncome, { isTotal: true }),
  ];

  const navRows = [
    row('NAV reconciliation', () => 0, { isSectionHeader: true }),
    row('Beginning NAV', (p) => p.navReconciliation.beginningNav, {
      isSubtleBackground: true,
    }),
    row('Net capital movement', (p) => p.navReconciliation.netCapitalMovement, {
      subtitle: 'Contributions minus distributions',
    }),
    row('Total expenses', (p) => p.navReconciliation.totalExpenses),
    row('Total income', (p) => p.navReconciliation.totalIncome),
    row('Realised gain / (loss)', (p) => p.navReconciliation.realisedGainLoss, {
      subtitle: 'In the period',
    }),
    row(
      'Change in unrealised gain / (loss)',
      (p) => p.navReconciliation.unrealisedGainLossChange,
      {
        subtitle: 'In the period',
      },
    ),
    row('Ending NAV', (p) => p.navReconciliation.endingNavNetOfCarry, {
      isTotal: true,
      subtitle: 'Net of carried interest',
    }),
  ];

  const carryRows = [
    row('Carried interest', () => 0, { isSectionHeader: true }),
    row(
      'Accrued carried interest',
      (p) => p.carriedInterest.accruedCarryAtStart,
      {
        subtitle: 'Start of period',
      },
    ),
    row(
      'Carried interest distributed',
      (p) => p.carriedInterest.carryDistributedInPeriod,
      {
        subtitle: 'In the period',
      },
    ),
    row(
      'Accrued carried interest earned / (lost)',
      (p) => p.carriedInterest.carryEarnedLostInPeriod,
      {
        subtitle: 'In the period',
      },
    ),
    row(
      'Accrued carried interest',
      (p) => p.carriedInterest.accruedCarryAtEnd,
      {
        subtitle: 'End of period',
      },
    ),
    row('Ending NAV', (p) => p.carriedInterest.endingNavGrossOfCarry, {
      isTotal: true,
      subtitle: 'Gross of carried interest',
    }),
  ];

  return [
    ...capitalMovementRows,
    ...expenseRows,
    ...incomeRows,
    ...navRows,
    ...carryRows,
  ];
}
